import { memo } from 'react';
import styled from '@emotion/styled';

import type { DividerProps, DividerVariant } from './Divider.types';
import type { Theme } from '../../theme';

/**
 * Horizontal divider with optional text
 */
const HorizontalDivider = styled.div<{
  theme?: Theme;
  $variant: DividerVariant;
  $hasChildren: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.border.light};
    ${({ $variant }) =>
      $variant === 'dashed' &&
      `
      background-color: transparent;
      background-image: linear-gradient(
        to right,
        currentColor 50%,
        transparent 50%
      );
      background-size: 8px 1px;
      color: inherit;
    `}
  }
  
  /* Hide pseudo-elements when no children (simple line) */
  ${({ $hasChildren }) =>
    !$hasChildren &&
    `
    &::after {
      display: none;
    }
    &::before {
      flex: none;
      width: 100%;
    }
  `}
`;

/**
 * Divider text content
 */
const DividerText = styled.span<{
  theme?: Theme;
}>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.tn};
  color: ${({ theme }) => theme.colors.text.muted};
  text-transform: uppercase;
  white-space: nowrap;
  letter-spacing: 0.5px;
`;

/**
 * Vertical divider
 */
const VerticalDivider = styled.div<{
  theme?: Theme;
  $variant: DividerVariant;
}>`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  min-height: 100%;
  width: 1px;
  background-color: ${({ theme, $variant }) =>
    $variant === 'solid' ? theme.colors.border.light : 'transparent'};
  
  ${({ $variant, theme }) =>
    $variant === 'dashed' &&
    `
    background-image: linear-gradient(
      to bottom,
      ${theme.colors.border.light} 50%,
      transparent 50%
    );
    background-size: 1px 8px;
  `}
`;

/**
 * Divider - A horizontal or vertical divider with optional text
 *
 * Use to separate content sections visually. Supports text in the middle
 * for horizontal orientation (e.g., "or", "and").
 *
 * @example
 * ```tsx
 * // Simple horizontal line
 * <Divider />
 *
 * // Horizontal divider with text
 * <Divider>or</Divider>
 *
 * // Dashed variant
 * <Divider variant="dashed">section</Divider>
 *
 * // Vertical divider (typically in flex row containers)
 * <div style={{ display: 'flex', gap: 16, height: 100 }}>
 *   <span>Left</span>
 *   <Divider orientation="vertical" />
 *   <span>Right</span>
 * </div>
 * ```
 */
const DividerComponent = ({
  children,
  orientation = 'horizontal',
  variant = 'solid',
  className,
  'data-testid': testId,
}: DividerProps) => {
  if (orientation === 'vertical') {
    return (
      <VerticalDivider
        className={className}
        $variant={variant}
        role="separator"
        aria-orientation="vertical"
        data-testid={testId}
      />
    );
  }

  const hasChildren = Boolean(children);

  return (
    <HorizontalDivider
      className={className}
      $variant={variant}
      $hasChildren={hasChildren}
      role="separator"
      aria-orientation="horizontal"
      data-testid={testId}
    >
      {hasChildren && <DividerText>{children}</DividerText>}
    </HorizontalDivider>
  );
};

DividerComponent.displayName = 'Divider';

export const Divider = memo(DividerComponent);

export default Divider;
